// Redux
import { Dispatch } from 'redux';

import { resetLeadsList } from 'Containers/AudienceExplore/actions/individuallist.actions';
// Classes
import { DemographicsReducerState } from '../Demographics/classes/demographics.classes';
import { ProspectProfileReducerState } from '../ProspectProfile/classes/prospectProfile.classes';
import { EngagementReducerState } from '../Engagement/classes/engagement.classes';
import { ListMembershipReducerState } from '../ListMembership/classes/listMembership.classes';
import { LocationsReducerState } from '../Locations/classes/locations.classes';

// Actions
import { initializeDemographicsState } from '../Demographics/actions/demographics.actions';
import { initializeProspectProfileState } from '../ProspectProfile/actions/prospectProfile.actions';
import { initializeEngagementState } from '../Engagement/actions/engagement.actions';
import { initializeListMembershipState } from '../ListMembership/actions/listMembership.actions';
import { setLocations } from '../Locations/actions/locations.action';

// Utilities
import { convertObjectValuesRecursive } from '../utilities/filter.utilities';

/**
 * @function initializeFilterState
 *
 * Sets the state of the entire Filter section using the four sub-states
 * you pass in
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {DemographicsReducerState} demographics - The new state
 * of the Demographics filter section.
 * @param {ProspectProfileReducerState} prospectProfile - The new state
 * of the Prospect Profile filter section.
 * @param {EngagementReducerState} engagement - The new state
 * of the Engagement filter section.
 * @param {ListMembershipReducerState} listMembership - The new state
 * of the List Membership filter section.
 * @return {void} - Doesn't return but dispatches the new Filter state to
 * the Redux store using various action-creators built for their respective
 * sub sections.
 *
 * @example
 *
 *     initializeFilterState(dispatch, demographics, prospectProfile, engagement, listMembership)
 */
export const initializeFilterState = (
  dispatch: Dispatch<any>,
  demographicsReducerState: DemographicsReducerState,
  prospectProfileReducerState: ProspectProfileReducerState,
  engagementReducerState: EngagementReducerState,
  listMembershipReducerState: ListMembershipReducerState,
  locationsReducerState: LocationsReducerState
) => {
  initializeDemographicsState(
    dispatch,
    demographicsReducerState.age,
    demographicsReducerState.ethnicity,
    demographicsReducerState.gender,
    demographicsReducerState.geography,
    demographicsReducerState.visitorType
  );
  initializeProspectProfileState(dispatch, prospectProfileReducerState);
  initializeEngagementState(dispatch, engagementReducerState);
  initializeListMembershipState(dispatch, listMembershipReducerState);
  setLocations(dispatch, locationsReducerState);
  dispatch(resetLeadsList({ viewType: 'Explore' }));
};

// Cache this and reuse
const emptyFilterState = {
  demographics: new DemographicsReducerState(),
  prospectProfile: new ProspectProfileReducerState(),
  engagement: new EngagementReducerState(),
  listMembership: new ListMembershipReducerState(),
  locations: new LocationsReducerState(),
};

export const clearFilterState = (dispatch: Dispatch<any>) => {
  initializeFilterState(
    dispatch,
    emptyFilterState.demographics,
    emptyFilterState.prospectProfile,
    emptyFilterState.engagement,
    emptyFilterState.listMembership,
    emptyFilterState.locations
  );
};

export const loadFilterStateFromAttributes = (
  dispatch: Dispatch<any>,
  filterAttributes: string
) => {
  const filterObj = JSON.parse(filterAttributes);

  if (!filterObj.locations) {
    filterObj.locations = { locationIds: [] };
  }
  // Parse out each property and pass to their appropriate classes, and initialize state
  const {
    demographics: { visitorType, geography, age, gender, ethnicity },
    prospectProfile: {
      intendedLevelOfStudy,
      entryTerm,
      intendedMajor,
      gpa,
      testScores,
    },
    engagement: {
      dateRegistered,
      relativeEngagement,
      totalTimeSpent,
      scheduledVisit,
      applied,
      activityWithInstitution,
      activityWithEAB,
    },
    listMembership: { lists },
    locations: { locationIds },
  } = filterObj;

  initializeFilterState(
    dispatch,
    convertObjectValuesRecursive(
      new DemographicsReducerState(
        visitorType,
        geography,
        age,
        gender,
        ethnicity
      ),
      null,
      undefined
    ),
    convertObjectValuesRecursive(
      new ProspectProfileReducerState(
        intendedLevelOfStudy,
        entryTerm,
        intendedMajor,
        gpa,
        testScores
      ),
      null,
      undefined
    ),
    convertObjectValuesRecursive(
      new EngagementReducerState(
        dateRegistered,
        relativeEngagement,
        totalTimeSpent,
        scheduledVisit,
        applied,
        activityWithInstitution,
        activityWithEAB
      ),
      null,
      undefined
    ),
    convertObjectValuesRecursive(
      new ListMembershipReducerState(lists),
      null,
      undefined
    ),
    locationIds
  );
};
try {
    // @ts-ignore
    clearFilterState.displayName = "clearFilterState";
    // @ts-ignore
    clearFilterState.__docgenInfo = { "description": "", "displayName": "clearFilterState", "props": { "apply": { "defaultValue": null, "description": "Calls the function, substituting the specified object for the this value of the function, and the specified array for the arguments of the function.\n@param thisArg The object to be used as the this object.\n@param argArray A set of arguments to be passed to the function.", "name": "apply", "required": true, "type": { "name": "(this: Function, thisArg: any, argArray?: any) => any" } }, "call": { "defaultValue": null, "description": "Calls a method of an object, substituting another object for the current object.\n@param thisArg The object to be used as the current object.\n@param argArray A list of arguments to be passed to the method.", "name": "call", "required": true, "type": { "name": "(this: Function, thisArg: any, ...argArray: any[]) => any" } }, "bind": { "defaultValue": null, "description": "For a given function, creates a bound function that has the same body as the original function.\nThe this object of the bound function is associated with the specified object, and has the specified initial parameters.\n@param thisArg An object to which the this keyword can refer inside the new function.\n@param argArray A list of arguments to be passed to the new function.", "name": "bind", "required": true, "type": { "name": "(this: Function, thisArg: any, ...argArray: any[]) => any" } }, "toString": { "defaultValue": null, "description": "Returns a string representation of a function.", "name": "toString", "required": false, "type": { "name": "() => string" } }, "prototype": { "defaultValue": null, "description": "", "name": "prototype", "required": true, "type": { "name": "any" } }, "length": { "defaultValue": null, "description": "", "name": "length", "required": true, "type": { "name": "number" } }, "arguments": { "defaultValue": null, "description": "", "name": "arguments", "required": true, "type": { "name": "any" } }, "caller": { "defaultValue": null, "description": "", "name": "caller", "required": true, "type": { "name": "Function" } }, "name": { "defaultValue": null, "description": "Returns the name of the function. Function names are read-only and can not be changed.", "name": "name", "required": true, "type": { "name": "string" } }, "__@hasInstance": { "defaultValue": null, "description": "Determines whether the given value inherits from this function if this function was used\nas a constructor function.\n\nA constructor function can control which objects are recognized as its instances by\n'instanceof' by overriding this method.", "name": "__@hasInstance", "required": true, "type": { "name": "(value: any) => boolean" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/shared/containers/Filter/actions/filter.actions.ts#clearFilterState"] = { docgenInfo: clearFilterState.__docgenInfo, name: "clearFilterState", path: "src/shared/containers/Filter/actions/filter.actions.ts#clearFilterState" };
}
catch (__react_docgen_typescript_loader_error) { }